import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Fade from "@mui/material/Fade";
import { Tooltip } from "@mui/material";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import { useTheme } from "@mui/material/styles";
import { useSetMessageProps } from "./hooks";

import { useState, useEffect } from "react";

import * as u from "./utility";
import { useNewTabText } from "./hooks";
import {
  DEFAULT_FONT_SIZE,
} from "./constants";

export function codeStyle(fontSize: number) {
  return {
    ml: 0,
    mr: 0,
    mt: 0.1,
    mb: 0.1,
    pt: 0.1,
    pb: 0.1,
    pr: 1,
    pl: 1,
    background: 'var(--code-bg)',
    color: 'var(--code-text)',
    fontSize: (11 * fontSize) / 13,
    borderRadius: "5px",
    display: "inline-block",
    width: "100%",
  };
}

type TextPreviewProps = {
  data?: string | ArrayBuffer | Uint8Array;
  item?: { st_ui?: any; SK: string; PK: string };
  onError?: () => void;
  disableControls?: boolean;
  fontSize?: number;
  fileName?: string;
  index?: number;
  mimetype?: string;
  propPrefix?: "codeblock";
  children?: (textData: string) => React.ReactNode;
};

const prefixedPropName = (propPrefix: string | undefined, propName: string) => {
  if (propPrefix) {
    return propPrefix + "_" + propName;
  }
  return propName;
};

export function TextPreview({
  data,
  item,
  onError,
  disableControls,
  fontSize,
  fileName,
  index,
  mimetype,
  propPrefix,
  children,
}: TextPreviewProps) {
  const theme = useTheme();
  const [maxHeight, setMaxHeight] = useState<number>(
    item && index !== undefined
      ? u.getUiProp(item, prefixedPropName(propPrefix, "height"), index, 300)
      : 300
  );
  const [anchor, setAnchor] = useState<HTMLElement>();
  const [textData, setTextData, handleDoubleClick] = useNewTabText();
  const [expand, setExpand] = useState<number>(
    item && index !== undefined
      ? u.getUiProp(item, prefixedPropName(propPrefix, "expand"), index, 0)
      : 0
  );
  const setMessageProps = useSetMessageProps();

  const MIN_HEIGHT = 40;

  useEffect(() => {
    if (item && index !== undefined) {
      setExpand(
        u.getUiProp(item, prefixedPropName(propPrefix, "expand"), index, 0)
      );
      setMaxHeight(
        u.getUiProp(item, prefixedPropName(propPrefix, "height"), index, 300)
      );
    } else {
      setExpand(0);
      setMaxHeight(300);
    }
  }, [item, index]);

  const decodeData = async () => {
    if (!data) {
      return;
    }

    if (typeof data === "string") {
      setTextData(data);
      return;
    }

    // Uint8Array for files downloaded from S3
    // ArrayBuffer for files uploaded in browser -- FileUploadPreview
    const encoding = u.detectTextEncoding(data);

    if (data && encoding) {
      try {
        let decoder = new TextDecoder(encoding);
        const decoded = decoder.decode(data);
        setTextData(decoded);
      } catch (e) {
        if (onError) {
          onError();
        }
      }
    } else if (data && !encoding) {
      if (onError) {
        onError();
      }
    }
  };

  useEffect(() => {
    decodeData();
  }, [data]);

  const [hover, setHover] = useState(false);

  const handleMouseEnter = () => {
    if (disableControls) {
      return;
    }

    setHover(true);
  };

  const handleMouseLeave = () => {
    if (disableControls) {
      return;
    }

    setHover(false);
    setAnchor(undefined);
  };

  const handleResize = (e: React.MouseEvent<HTMLElement>) => {
    if (disableControls) {
      return;
    }

    e.preventDefault();
    let start = e.pageY;
    const initHeight = maxHeight;

    function onMouseMove(e: MouseEvent) {
      const newHeight = initHeight - start + e.pageY;
      if (newHeight > MIN_HEIGHT) {
        setMaxHeight(newHeight);
      }
    }
    function onMouseUp(e: MouseEvent) {
      document.body.removeEventListener("mousemove", onMouseMove);
      document.body.removeEventListener("mouseup", onMouseUp);
      setMaxHeight((height) => {
        if (item && index !== undefined) {
          const propName = prefixedPropName(propPrefix, "height");
          setMessageProps(item, `st_ui/${propName}/${index}`, height);
        }
        return height;
      });
    }

    document.body.addEventListener("mousemove", onMouseMove);
    document.body.addEventListener("mouseup", onMouseUp);
  };

  const handleDownload = () => {
    var link = document.createElement("a");
    link.href = `data:${mimetype},` + encodeURIComponent(textData);
    link.download = fileName || "file.txt";
    link.click();
  };

  const handleExpand = () => {
    if (item && index !== undefined) {
      const propName = prefixedPropName(propPrefix, "expand");
      setMessageProps(item, `st_ui/${propName}/${index}`, expand ? 0 : 1);
    }
    setExpand((prev) => 1 - prev);
  };

  return (
    <Box
      component="span"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ width: "100%" }}
    >
      <Box
        sx={{ position: "relative", display: "inline-block", width: "100%" }}
      >
        {hover && (
          <>
            <Box
              sx={{
                position: "absolute",
                left: 4,
                top: 4,
                zIndex: 5,
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                px: 0.5,
                py: 0.25,
                borderRadius: "12px",
                background: theme.palette.mode === 'dark'
                  ? 'rgba(0, 0, 0, 0.6)'
                  : 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
              }}
            >
              <IconButton
                onClick={handleExpand}
                size="small"
                sx={{
                  p: 0.25,
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                  '&:hover': {
                    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                {expand ? (
                  <ZoomInMapIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ZoomOutMapIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
              <IconButton
                onClick={handleDownload}
                size="small"
                sx={{
                  p: 0.25,
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                  '&:hover': {
                    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <DownloadIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  navigator.clipboard.writeText(textData);
                  setAnchor(e.currentTarget);
                  setTimeout(() => {
                    setAnchor(undefined);
                  }, 1000);
                }}
                size="small"
                sx={{
                  p: 0.25,
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                  '&:hover': {
                    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Popper
              open={anchor != null}
              anchorEl={anchor}
              placement="right-start"
              transition
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  <Paper>
                    <Typography fontSize={10} sx={{ p: 1 }}>
                      copied!
                    </Typography>
                  </Paper>
                </Fade>
              )}
            </Popper>

            {!expand && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 2,
                  zIndex: 10,
                  cursor: "ns-resize",
                  height: 14,
                  width: "100%",
                  backgroundColor: "black",
                  opacity: "30%",
                }}
                onMouseDown={handleResize}
              ></Box>
            )}
          </>
        )}

        <Box
          sx={{
            ...codeStyle(fontSize || DEFAULT_FONT_SIZE),
            maxHeight: expand ? undefined : maxHeight,
            minHeight: MIN_HEIGHT,
            overflow: "auto",
            position: "relative",
            pt: 0.5,
            pb: 0.5,
          }}
          onClick={(e) => handleDoubleClick(e, children)}
        >
          <Stack direction={"row"}>
            <pre
              style={{
                width: "100%",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {!children && <>{textData}</>}
              {children && <>{children(textData)}</>}
            </pre>
            <Box sx={{ width: "50px" }}></Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
